<template>
    <div class="mx-auto max-w-[600px] px-3 pt-6 sm:px-6 sm:pt-12">
        <!-- Page Header -->
        <div class="flex items-center gap-2 mb-16">
            <Settings class="size-6 text-new-neutral-800" />
            <h1 class="text-3xl text-new-neutral-800">Settings</h1>
        </div>

        <!-- Profile Header -->
        <div class="flex items-center gap-4 mb-9">
            <div class="flex size-[100px] items-center justify-center rounded-full border border-new-neutral-300 bg-white">
                <img
                    v-if="user.profile_photo_path"
                    :src="user.profile_photo_path"
                    class="size-[100px] rounded-full object-cover"
                />
                <span v-else class="text-3xl font-semibold text-new-neutral-400">
                    {{ initials }}
                </span>
            </div>
            <div class="space-y-1.5">
                <p class="text-xl font-semibold text-foreground">{{ user.name }}</p>
                <p class="text-sm text-muted-foreground">{{ user.email }}</p>
            </div>
        </div>

        <!-- Tabs -->
        <Tabs :model-value="activeTab" class="w-full mb-8" @update:model-value="onTabChange">
            <TabsList class="h-auto w-full">
                <TabsTrigger value="profile" class="h-auto flex-1">Profile</TabsTrigger>
                <TabsTrigger value="api-tokens" class="h-auto flex-1">API tokens</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" class="space-y-4 pb-12 mt-4">
                <IncomingInvitationsCard />
                <PersonalInfoCard :has-password="hasPassword" />
                <PasswordCard :has-password="hasPassword" />
                <TwoFactorCard :has-password="hasPassword" />
                <BrowserSessionsCard :sessions="sessions" :has-password="hasPassword" />
                <AppTypeCard />
            </TabsContent>

            <TabsContent value="api-tokens" class="pb-12 mt-4">
                <ApiTokensCard :tokens="tokens" />
            </TabsContent>
        </Tabs>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, PropType } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { Settings } from 'lucide-vue-next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ISession from '@/types/Settings/ISession';
import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue';
import PersonalInfoCard from './components/PersonalInfoCard.vue';
import PasswordCard from './components/PasswordCard.vue';
import TwoFactorCard from './components/TwoFactorCard.vue';
import BrowserSessionsCard from './components/BrowserSessionsCard.vue';
import AppTypeCard from './components/AppTypeCard.vue';
import ApiTokensCard from './components/ApiTokensCard.vue';
import IncomingInvitationsCard from './components/IncomingInvitationsCard.vue';

interface IApiToken {
    id: number;
    name: string;
    abilities: string[];
}

defineOptions({
    layout: DefaultLayoutV2,
});

type SettingsTab = 'profile' | 'api-tokens';

const props = defineProps({
    sessions: {
        type: Array as PropType<Array<ISession>>,
        required: true,
    },
    has_password: {
        type: Boolean,
        required: true,
    },
    tokens: {
        type: Array as PropType<IApiToken[]>,
        default: () => [],
    },
    initial_tab: {
        type: String as PropType<SettingsTab>,
        default: 'profile',
    },
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const user = computed(() => globalProps.value.user);
const hasPassword = computed(() => props.has_password);
const activeTab = ref<SettingsTab>(props.initial_tab);

const onTabChange = (value: string | number) => {
    const tab = value as SettingsTab;
    if (tab === activeTab.value) return;
    activeTab.value = tab;
    const target = tab === 'api-tokens'
        ? route('settings.api-tokens.index')
        : route('settings.profile-settings.index');
    router.get(target, {}, { preserveScroll: true, preserveState: false });
};

const initials = computed(() => {
    const parts = user.value.name.split(' ');
    return parts.map((p: string) => p[0]?.toUpperCase()).join('').slice(0, 2);
});
</script>
